import React from 'react';

const DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const WEEK_DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

export const isoDateToBolivian = (value?: string | null) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

export const bolivianDateToIso = (value: string): string | null => {
  const match = DATE_PATTERN.exec(value.trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
};

const calendarDays = (anchor: Date) => {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const firstMondayOffset = (first.getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, index) => new Date(anchor.getFullYear(), anchor.getMonth(), index - firstMondayOffset + 1));
};

interface BolivianDateInputProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  style?: React.CSSProperties;
  className?: string;
  'data-testid'?: string;
}

export function BolivianDateInput({
  value,
  onChange,
  required = false,
  disabled = false,
  name,
  id,
  style,
  className,
  'data-testid': testId
}: BolivianDateInputProps) {
  const isoValue = value ? value.slice(0, 10) : '';
  const [draft, setDraft] = React.useState(() => isoDateToBolivian(isoValue));
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selected = bolivianDateToIso(draft) || isoValue;
  const selectedDate = selected ? new Date(`${selected}T12:00:00`) : null;
  const [anchor, setAnchor] = React.useState(() => selectedDate || new Date());

  React.useEffect(() => {
    const display = isoDateToBolivian(isoValue);
    if (bolivianDateToIso(draft) || draft === '') setDraft(display);
  // Keep an in-progress invalid edit visible until the user corrects it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoValue]);

  React.useEffect(() => {
    inputRef.current?.setCustomValidity(error);
  }, [error]);

  const validationMessage = (candidate: string) => {
    if (!candidate.trim()) return required ? 'Fecha obligatoria.' : '';
    return bolivianDateToIso(candidate) ? '' : 'Use una fecha válida: DD/MM/YYYY.';
  };

  const updateDraft = (candidate: string) => {
    setDraft(candidate);
    inputRef.current?.setCustomValidity(validationMessage(candidate));
    const iso = bolivianDateToIso(candidate);
    if (candidate === '') onChange('');
    if (iso) {
      onChange(iso);
      setError('');
    }
  };

  const selectDate = (date: Date) => {
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setDraft(isoDateToBolivian(iso));
    onChange(iso);
    setError('');
    inputRef.current?.setCustomValidity('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const openCalendar = () => {
    const current = bolivianDateToIso(draft) || isoValue;
    setAnchor(current ? new Date(`${current}T12:00:00`) : new Date());
    setOpen(true);
  };

  const yearOptions = Array.from({ length: 121 }, (_, index) => new Date().getFullYear() - 100 + index);
  const days = calendarDays(anchor);
  const today = new Date();

  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', minWidth: 0 }}>
      <span className="responsive-row" style={{ display: 'flex', gap: 4 }}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/YYYY"
          value={draft}
          required={required}
          disabled={disabled}
          pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}"
          className={className}
          data-testid={testId}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id || name || testId || 'bolivian-date'}-error` : undefined}
          onChange={event => updateDraft(event.target.value)}
          onBlur={() => setError(validationMessage(draft))}
          onKeyDown={event => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'ArrowDown' && !open) {
              event.preventDefault();
              openCalendar();
            }
          }}
          style={{ ...style, flex: 1 }}
        />
        <button type="button" disabled={disabled} aria-label="Abrir calendario" onMouseDown={event => event.preventDefault()} onClick={() => open ? setOpen(false) : openCalendar()} style={{ border: '1px solid currentColor', borderRadius: 4, background: 'transparent', color: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 7px' }}>▣</button>
      </span>
      {error && <small id={`${id || name || testId || 'bolivian-date'}-error`} role="alert" style={{ color: '#dc2626', marginTop: 3 }}>{error}</small>}
      {open && (
        <span role="dialog" aria-label="Calendario" style={{ position: 'absolute', zIndex: 10000, top: 'calc(100% + 4px)', left: 0, width: 280, padding: 10, border: '1px solid #64748b', borderRadius: 8, background: '#fff', color: '#0f172a', boxShadow: '0 8px 20px rgba(0,0,0,.25)' }}>
          <span className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <button type="button" aria-label="Mes anterior" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}>‹</button>
            <select aria-label="Mes" value={anchor.getMonth()} onChange={event => setAnchor(new Date(anchor.getFullYear(), Number(event.target.value), 1))}>{MONTHS.map((month, index) => <option key={month} value={index}>{month}</option>)}</select>
            <select aria-label="Año" value={anchor.getFullYear()} onChange={event => setAnchor(new Date(Number(event.target.value), anchor.getMonth(), 1))}>{yearOptions.map(year => <option key={year} value={year}>{year}</option>)}</select>
            <button type="button" aria-label="Mes siguiente" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}>›</button>
          </span>
          <span className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center', fontSize: 12 }}>
            {WEEK_DAYS.map(day => <strong key={day}>{day}</strong>)}
            {days.map(day => {
              const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
              const active = iso === selected;
              const isToday = day.toDateString() === today.toDateString();
              return <button key={iso} type="button" aria-label={isoDateToBolivian(iso)} onClick={() => selectDate(day)} style={{ border: active ? '1px solid #2563eb' : '1px solid transparent', borderRadius: 4, background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : day.getMonth() === anchor.getMonth() ? '#0f172a' : '#94a3b8', fontWeight: isToday ? 700 : 400, padding: 5 }}>{day.getDate()}</button>;
            })}
          </span>
          <button type="button" onClick={() => selectDate(today)} style={{ marginTop: 8 }}>Hoy</button>
        </span>
      )}
    </span>
  );
}

interface BolivianDateTimeInputProps extends Omit<BolivianDateInputProps, 'onChange'> {
  onChange: (value: string) => void;
}

export function BolivianDateTimeInput({ value, onChange, required, disabled, name, id, style, className, 'data-testid': testId }: BolivianDateTimeInputProps) {
  const dateValue = value.slice(0, 10);
  const timeValue = /^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/.exec(value)?.[1] || '';
  const [dateDraft, setDateDraft] = React.useState(dateValue);
  const [timeDraft, setTimeDraft] = React.useState(timeValue);
  const [timeError, setTimeError] = React.useState('');
  const timeRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => { if (value) setDateDraft(dateValue); }, [dateValue, value]);
  React.useEffect(() => { if (value) setTimeDraft(timeValue); }, [timeValue, value]);
  React.useEffect(() => { timeRef.current?.setCustomValidity(timeError); }, [timeError]);

  const combine = (date: string, time: string) => {
    if (date && time) onChange(`${date}T${time}`);
    if (!date && !time) onChange('');
  };
  const validateTime = (candidate: string) => !candidate ? (required ? 'Hora obligatoria.' : '') : TIME_PATTERN.test(candidate) ? '' : 'Use hora válida: HH:mm.';

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'flex-start', minWidth: 0 }}>
      <BolivianDateInput value={dateDraft} onChange={date => { setDateDraft(date); combine(date, timeDraft); }} required={required} disabled={disabled} name={name} id={id} style={style} className={className} data-testid={testId} />
      <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
        <input ref={timeRef} type="text" inputMode="numeric" autoComplete="off" placeholder="HH:mm" value={timeDraft} required={required} disabled={disabled} pattern="(?:[01][0-9]|2[0-3]):[0-5][0-9]" aria-invalid={Boolean(timeError)} onChange={event => { const next = event.target.value; setTimeDraft(next); timeRef.current?.setCustomValidity(validateTime(next)); if (TIME_PATTERN.test(next)) { combine(dateDraft, next); setTimeError(''); } }} onBlur={() => setTimeError(validateTime(timeDraft))} onKeyDown={event => { if (event.key === 'Escape') event.currentTarget.blur(); }} style={{ ...style, width: 72 }} />
        {timeError && <small role="alert" style={{ color: '#dc2626', marginTop: 3 }}>{timeError}</small>}
      </span>
    </span>
  );
}

interface BolivianTimeInputProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export function BolivianTimeInput({ value, onChange, required = false, disabled = false, style }: BolivianTimeInputProps) {
  const [draft, setDraft] = React.useState(value);
  const [error, setError] = React.useState('');
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => setDraft(value), [value]);
  React.useEffect(() => { ref.current?.setCustomValidity(error); }, [error]);
  const validate = (candidate: string) => !candidate ? (required ? 'Hora obligatoria.' : '') : TIME_PATTERN.test(candidate) ? '' : 'Use hora válida: HH:mm.';
  return <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
    <input ref={ref} type="text" inputMode="numeric" autoComplete="off" placeholder="HH:mm" value={draft} required={required} disabled={disabled} pattern="(?:[01][0-9]|2[0-3]):[0-5][0-9]" aria-invalid={Boolean(error)} onChange={event => { const next = event.target.value; setDraft(next); ref.current?.setCustomValidity(validate(next)); if (TIME_PATTERN.test(next)) { onChange(next); setError(''); } if (!next) onChange(''); }} onBlur={() => setError(validate(draft))} style={style} />
    {error && <small role="alert" style={{ color: '#dc2626', marginTop: 3 }}>{error}</small>}
  </span>;
}
